import { Box, Typography, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AppShell from '../components/layout/AppShell';
import NearbyLeafletMap from '../components/map/NearbyLeafletMap';
import DesignSystem from '../designSystem';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n/useT';
import { fetchUserCategories } from '../services/api';
import { fixedPinsFromBusinesses } from '../utils/fixedBusinessPins';
import { resolveHomeViewerLocation, VIEWER_LOCATION_CHANGED } from '../utils/geolocation';
import { pinsWithinRadius } from '../utils/nearbyMapViewport';

const LOCATION_TIMEOUT_MS = 8000;

export default function NearbyMapPage() {
  const { user } = useAuth();
  const token = user?.token;
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [statusText, setStatusText] = useState(() => t('mapLocating'));
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [pins, setPins] = useState(() => [] as ReturnType<typeof fixedPinsFromBusinesses>);

  const loadMapData = useCallback(async () => {
    if (!token) {
      setStatusText(t('mapAuthRequired'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setStatusText(t('mapLocating'));
    try {
      const viewer = await resolveHomeViewerLocation({ timeoutMs: LOCATION_TIMEOUT_MS });
      const lat = Number(viewer.coords.lat);
      const lng = Number(viewer.coords.lng);
      setCenter({ lat, lng });

      const data = await fetchUserCategories(undefined, token, viewer.coords.lat, viewer.coords.lng, 1, undefined, {
        page: 1,
        limit: 50,
      });
      const allPins = fixedPinsFromBusinesses(data.businesses ?? []);
      const local = pinsWithinRadius(allPins, { lat, lng });
      setPins(local);
      setStatusText(
        local.length === 0 ? t('mapFixedEmpty') : t('mapFixedCountLocal', { count: local.length })
      );
    } catch {
      setStatusText(t('mapLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    loadMapData();
  }, [loadMapData]);

  useEffect(() => {
    const onLoc = () => {
      loadMapData();
    };
    window.addEventListener(VIEWER_LOCATION_CHANGED, onLoc);
    return () => window.removeEventListener(VIEWER_LOCATION_CHANGED, onLoc);
  }, [loadMapData]);

  const mapPins = useMemo(() => pins, [pins]);

  return (
    <AppShell title={t('navMap')} maxWidth={false} showCompanion={false} hideDesktopHeader={false}>
      <Box
        id="tab.map"
        data-testid="tab.map"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'calc(100dvh - 140px)', md: 'calc(100vh - 120px)' },
          minHeight: 420,
          mx: { xs: -2, md: 0 },
        }}
      >
        <Box
          sx={{
            mx: { xs: 2, md: 0 },
            mb: 1,
            px: 2,
            py: 1,
            borderRadius: DesignSystem.borderRadius.lg,
            bgcolor: DesignSystem.colors.surface,
            border: `1px solid ${DesignSystem.colors.separator}`,
            textAlign: 'center',
          }}
        >
          <Typography
            id="map.fixedStatus"
            data-testid="map.fixedStatus"
            sx={{ fontSize: 14, color: DesignSystem.colors.textPrimary, lineHeight: 1.35 }}
          >
            {statusText}
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', flex: 1, minHeight: 320, borderRadius: { md: DesignSystem.borderRadius.lg }, overflow: 'hidden' }}>
          {loading || !center ? (
            <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <NearbyLeafletMap center={center} pins={mapPins} />
          )}
        </Box>
      </Box>
    </AppShell>
  );
}
